/**
 * Lightweight CSV export utility.
 * - `columns`: array of { key, label, format? } — `format(row)` returns the cell value for that column.
 *   If `key` is provided without `format`, `row[key]` is used.
 * - Generates a UTF-8 BOM-prefixed CSV (opens cleanly in Excel).
 * - Safely quotes values, escapes embedded quotes, and flattens line breaks.
 */
const CSV_NEWLINE = '\r\n';

function escapeCell(value) {
  if (value === null || value === undefined) return '';
  let str;
  if (value instanceof Date) {
    str = value.toISOString();
  } else if (typeof value === 'object') {
    try { str = JSON.stringify(value); } catch { str = String(value); }
  } else {
    str = String(value);
  }
  // Replace internal CR/LF with space to avoid broken rows
  str = str.replace(/\r?\n/g, ' ');
  const needsQuote = /[",\t]/.test(str);
  if (needsQuote) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

export function rowsToCsv(rows, columns) {
  if (!Array.isArray(columns) || columns.length === 0) {
    throw new Error('columns required');
  }
  const header = columns.map(c => escapeCell(c.label || c.key)).join(',');
  const body = (rows || []).map(row =>
    columns
      .map(col => escapeCell(col.format ? col.format(row) : row?.[col.key]))
      .join(',')
  );
  return [header, ...body].join(CSV_NEWLINE);
}

export function downloadCsv(rows, columns, filename = 'export.csv') {
  const csv = rowsToCsv(rows, columns);
  // UTF-8 BOM so Excel auto-detects encoding
  const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const safeName = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  link.download = safeName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Format Date string to YYYY-MM-DD for filenames. */
export function todayStamp() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
